import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Catalogue des actes courants d'un laboratoire d'analyse medicale (cahier
 * des charges labo, section 3 : biologie medicale). Port de ActTypesModule
 * (frontend/src/pages/HopitalDashboard.js) sur la route dédiée
 * /api/lab/act-types (backend/routes/lab/act_types.py) — simplifié : la
 * branche `isLab` de l'original est toujours vraie ici, donc retirée.
 */
const LAB_ACT_CATALOG = [
  { name: "Prise de sang", duration: 10, prep: "" },
  { name: "Numeration Formule Sanguine (NFS)", duration: 10, prep: "" },
  { name: "Glycemie a jeun", duration: 10, prep: "A jeun depuis 8h" },
  { name: "Hemoglobine glyquee (HbA1c)", duration: 10, prep: "" },
  { name: "Bilan lipidique (cholesterol, triglycerides)", duration: 10, prep: "A jeun depuis 12h" },
  { name: "Bilan renal (uree, creatinine)", duration: 10, prep: "" },
  { name: "Bilan hepatique (ALAT, ASAT, GGT)", duration: 10, prep: "" },
  { name: "Ionogramme sanguin", duration: 10, prep: "" },
  { name: "TSH (bilan thyroidien)", duration: 10, prep: "" },
  { name: "CRP (proteine C-reactive)", duration: 10, prep: "" },
  { name: "Groupage sanguin ABO / Rhesus", duration: 10, prep: "" },
  { name: "Taux de prothrombine / INR (TP-INR)", duration: 10, prep: "" },
  { name: "Ferritine", duration: 10, prep: "" },
  { name: "Vitamine D", duration: 10, prep: "" },
  { name: "Serologies (VIH, hepatites B et C)", duration: 10, prep: "" },
  { name: "Beta-HCG (test de grossesse)", duration: 10, prep: "" },
  { name: "Examen cytobacteriologique des urines (ECBU)", duration: 5, prep: "Recueil au premier jet du matin, toilette prealable" },
  { name: "Coproculture (analyse des selles)", duration: 5, prep: "" },
  { name: "Spermogramme", duration: 15, prep: "Abstinence de 3 a 5 jours" },
  { name: "Prelevement vaginal", duration: 15, prep: "" },
  { name: "Test d'allergie", duration: 20, prep: "" },
];

export default function ActTypesModule({ can }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [customAct, setCustomAct] = useState(false);
  const empty = { name: "", duration_minutes: "15", personnel_requis: "", ressources: "", instructions: "", preparation: "" };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/lab/act-types`, { ...getAuthConfig(), params: { include_inactive: true } })
      .then((r) => setItems(r.data.act_types || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(empty); setEditing(null); setError(""); setCustomAct(false); };

  const save = async () => {
    if (!form.name.trim()) { setError("Le nom de l'acte est obligatoire"); return; }
    const dur = parseInt(form.duration_minutes, 10);
    if (!Number.isFinite(dur) || dur < 1) { setError("La durée doit être un nombre valide (au moins 1 minute)"); return; }
    setSaving(true); setError("");
    const payload = { ...form, duration_minutes: dur };
    try {
      if (editing) await axios.put(`${API_URL}/api/lab/act-types/${editing}`, payload, getAuthConfig());
      else await axios.post(`${API_URL}/api/lab/act-types`, payload, getAuthConfig());
      setShowForm(false); resetForm(); load();
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const startEdit = (a) => {
    setEditing(a.act_type_id);
    setForm({
      name: a.name || "", duration_minutes: String(a.duration_minutes || 15),
      personnel_requis: a.personnel_requis || "", ressources: a.ressources || "",
      instructions: a.instructions || "", preparation: a.preparation || "",
    });
    setCustomAct(!!a.name && !LAB_ACT_CATALOG.some((x) => x.name === a.name));
    setError(""); setShowForm(true);
  };

  const toggleActif = async (a) => {
    try { await axios.put(`${API_URL}/api/lab/act-types/${a.act_type_id}`, { actif: !a.actif }, getAuthConfig()); load(); }
    catch (e) { setError("Erreur"); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try { await axios.delete(`${API_URL}/api/lab/act-types/${toDelete.act_type_id}`, getAuthConfig()); setToDelete(null); load(); }
    catch (e) { setError("Erreur lors de la suppression"); setToDelete(null); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Types d'actes</h1>
        {can("acts:write") && <button onClick={() => { resetForm(); setShowForm(true); }} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau type d'acte</button>}
      </div>

      {error && !showForm && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #B5D4F4", borderRadius: 12, padding: 20, marginTop: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14, color: "#0F172A" }}>{editing ? "Modifier le type d'acte" : "Nouveau type d'acte"}</div>
          {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <select style={iS} value={customAct ? "__autre__" : (LAB_ACT_CATALOG.some((a) => a.name === form.name) ? form.name : "")}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__autre__") { setCustomAct(true); setForm({ ...form, name: "" }); }
                  else if (v === "") { setCustomAct(false); setForm({ ...form, name: "" }); }
                  else {
                    setCustomAct(false);
                    const item = LAB_ACT_CATALOG.find((a) => a.name === v);
                    // Correctif L-ACTTYPE-DURATION : la duree du catalogue
                    // n'etait jamais appliquee au formulaire, qui gardait sa
                    // duree par defaut (15 min) meme pour un acte catalogue a
                    // 5 ou 10 min — faussait ensuite le calcul des creneaux
                    // d'agenda pour cet acte.
                    setForm({ ...form, name: item.name, preparation: form.preparation || item.prep,
                              duration_minutes: String(item.duration) });
                  }
                }}>
                <option value="">Choisir un acte...</option>
                {LAB_ACT_CATALOG.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                <option value="__autre__">Autre (acte non repertorie)...</option>
              </select>
              {customAct && (
                <input style={{ ...iS, marginTop: 8 }} placeholder="Nom de l'acte *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
              )}
            </div>
            <input style={iS} type="number" min="1" placeholder="Duree (minutes)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
            <input style={iS} placeholder="Personnel requis (ex: Préleveur)" value={form.personnel_requis} onChange={(e) => setForm({ ...form, personnel_requis: e.target.value })} />
            <input style={iS} placeholder="Ressources (ex: Salle, tube EDTA)" value={form.ressources} onChange={(e) => setForm({ ...form, ressources: e.target.value })} />
          </div>
          <textarea style={{ ...iS, marginTop: 12, minHeight: 50, resize: "vertical" }} placeholder="Consignes (optionnel)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <textarea style={{ ...iS, marginTop: 12, minHeight: 50, resize: "vertical" }} placeholder="Préparation demandée au patient (ex: A jeun depuis 8h)" value={form.preparation} onChange={(e) => setForm({ ...form, preparation: e.target.value })} />
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>{saving ? "Enregistrement..." : "Enregistrer"}</button>
            <button onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: "8px 18px", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Chargement...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>Aucun type d'acte. Cliquez sur « Nouveau type d'acte ».</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                {["Acte", "Durée", "Personnel", "Ressources", "Statut", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.act_type_id} style={{ borderTop: "1px solid #F1F5F9", opacity: a.actif ? 1 : 0.55 }}>
                  <td style={{ padding: "12px 16px", fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{a.name}{a.preparation && <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 400 }}>{a.preparation}</div>}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.duration_minutes} min</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.personnel_requis || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#475569" }}>{a.ressources || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: a.actif ? "#DCFCE7" : "#F1F5F9", color: a.actif ? "#166534" : "#64748B" }}>{a.actif ? "Actif" : "Inactif"}</span></td>
                  <td style={{ padding: "12px 16px" }}><span style={{ display: "flex", gap: 10 }}>
                    {can("acts:write") && <button onClick={() => startEdit(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Modifier</button>}
                    {can("acts:write") && <button onClick={() => toggleActif(a)} style={{ fontSize: 13, color: "#334155", background: "none", border: "none", cursor: "pointer" }}>{a.actif ? "Désactiver" : "Activer"}</button>}
                    {can("acts:write") && <button onClick={() => setToDelete(a)} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Supprimer</button>}
                  </span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toDelete && (
        <div onClick={() => setToDelete(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "90%", maxWidth: 380, padding: 22 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Supprimer « {toDelete.name} » ?</div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 18 }}>Cette action est définitive.</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setToDelete(null)} style={{ padding: "8px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Annuler</button>
              <button onClick={doDelete} style={{ padding: "8px 16px", background: "#A32D2D", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
